import React, { useMemo, useState } from 'react';
import { Icon } from '@shakkerai/components';
import { Tooltip } from '@mantine/core';
import { Collapse } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { translate } from '@shakkerai/util-i18n';
import { copy } from '@shakkerai/util-copy';
import { notifyError, notifySuccess } from '@shakkerai/pro-components';
import { config } from '@shakkerai/config';
import { useElementGenInfo } from '@shakkerai/pro-hooks';
import { controlNetList, controlnetMap, samplerMethodNamesMap } from '@shakkerai/pro-constants';
import { applyImg2ImgSourceImage, applyGenerateParams } from '@starflow/app/modules/generator/helpers';
import { getActiveGeneratorId, insertGalleryImage, applyModel, applyPresetModel } from '@shakkerai/pro-utils';
import { reproduceCollage, reproduceUpscale, reproduceInpaint, reproduceOutpaint } from '@shakkerai/pro-utils';
import { convert2CompressedUrl } from '@shakkerai/util-image';
import { ImageShareButton } from '@shakkerai/pro-components';
import { MODEL_FLUX, presetCheckpointsMap, communityUrl } from '@shakkerai/pro-models';
import { BASETYPE_VALUE_TO_SHORT_LABEL, BASETYPE_VALUE_TO_NAME, BASETYPE_NAME_TO_VALUE } from '@shakkerai/pro-models';
import { getProjectState, useTempState, setTempState, useProjectState } from '@starflow/app/store';
import { upscaleOptions } from '../../../../upscale/components/_legacy';
import { FrameSize, SizeInputGroup } from '../size';
import { CustomModelCard, PresetModelCard } from '../model.jsx';
import iconLoraRecommendCN from '../assets/lora_recommend_cn.svg';
import iconLoraRecommendEN from '../assets/lora_recommend_en.svg';
import crownIcon from '../assets/vip-icon.png';
import cs from 'classnames';
import { getFieldViewComponent } from '@starflow/app/modules/workflow/components/fields';
import { enterWorkflowMode } from '@starflow/app/modules/workflow/helpers/helpers.js';
import styles from './index.module.scss';
import { requestModelVersionDetail } from '@shakkerai/services/src/pro.js';
import classNames from 'classnames';

const isOverseas = config.env === 'aws';
const iconLoraRecommend = isOverseas ? iconLoraRecommendEN : iconLoraRecommendCN;
const paramsNames = ['samplerMethod', 'samplingSteps'];

const reproduceButtonLabels = {
  inpaint: translate('image_info_reproduce_inpaint'),
  upscale: translate('image_info_reproduce'),
  outpaint: translate('image_info_reproduce'),
  collage: translate('image_info_reproduce'),
  'collage-group': translate('image_info_reproduce'),
  'collage-elements': translate('image_info_reproduce'),
};

const InpaintSourceImage = ({ sourceImage, inpaintData }) => {
  const { selections = [] } = inpaintData || {};
  // selections: {points: [x,y][]}[]

  const maxWidth = 268;
  const maxHeight = 200;

  const scale = Math.min(maxWidth / sourceImage.width, maxHeight / sourceImage.height);
  const inpaintStyle = {
    width: sourceImage.width * scale,
    height: sourceImage.height * scale,
  };

  return (
    <div className="flex items-center justify-center mt-1 mb-3 bg-[#ececec]">
      <div style={inpaintStyle} className="relative">
        <img src={convert2CompressedUrl(sourceImage.url, 512)} className="absolute" />
        <svg width={sourceImage.width * scale} height={sourceImage.height * scale} className="relative z-10">
          {selections.map((item, index) => {
            return (
              <polygon
                key={index}
                fill="#8053ff90"
                points={item.points.map((p) => `${p[0] * scale},${p[1] * scale}`).join(' ')}
              />
            );
          })}
        </svg>
      </div>
    </div>
  );
};

const CollageSourceImages = ({ collageData }) => {
  if (!collageData) return null;

  const { width, height, images } = collageData;
  const maxWidth = 268;
  const maxHeight = 200;

  const scale = Math.min(maxWidth / width, maxHeight / height);
  const collageStyle = {
    width: width * scale,
    height: height * scale,
  };

  return (
    <div className="flex items-center justify-center mt-1 mb-3 bg-[#ececec]">
      <div style={collageStyle} className="relative bg-white">
        {(images || []).map((item, index) => {
          return (
            <img
              key={index}
              src={convert2CompressedUrl(item.url, 536)}
              draggable={false}
              className="absolute"
              style={{
                left: item.x * scale,
                top: item.y * scale,
                width: item.width * (item.scaleX || 1) * scale,
                height: item.height * (item.scaleY || 1) * scale,
              }}
            />
          );
        })}
      </div>
    </div>
  );
};

export const GenerationInfoPanelInner = ({
  withHeader = true,
  withFooter = true,
  withSourceImage = true,
  withSendButtons = true,
  noReproduce,
  image,
  genInfo = {},
  imageWidth,
  imageHeight,
  onClose,
}) => {
  const isSimple = useTempState('globalMode') === 'simple';
  const [showAppliedTip, setShowAppliedTip] = useState(false);

  const acturalSeed = useMemo(() => {
    if (!genInfo) return null;

    if (genInfo.isGalleryImage) {
      return null;
    }

    if (genInfo.acturalSeed) {
      return genInfo.acturalSeed;
    }

    if (genInfo.pngInfo) {
      // 'xxxx Seed: 3170774853,xxxx'
      return genInfo.pngInfo.match(/Seed: (\d+),/)?.[1];
    }

    return null;
  }, [genInfo]);

  const [opened, { toggle }] = useDisclosure(false);

  const isInpaint = genInfo.generateType === 'inpaint';
  const isUpscale = genInfo.generateType === 'upscale';
  const isCollage = genInfo.generateType?.startsWith?.('collage');
  const isOutpaint = genInfo.generateType === 'outpaint';

  const inpaintData = genInfo.inpaintData;
  const upscaleData = genInfo.upscaleData;
  const collageData = genInfo.collageData;
  const outpaintData = genInfo.outpaintData || image.outpaintRect;

  const acturalPrompt = genInfo.acturalPrompt;
  const baseModel = genInfo.checkpoint;
  const presetBaseModelId = genInfo.presetBaseModelId;
  const isStarFlow = presetBaseModelId === 'base_checkpoint';

  const loraModels = useMemo(() => {
    const baseType = baseModel ? baseModel.baseType : presetCheckpointsMap[presetBaseModelId]?.versionDetail?.baseType;

    const allLoraModels = globalThis.STARFLOW_DEBUG_ENABLED
      ? genInfo.loraModels
      : genInfo.loraModels?.filter((item) => !item.isHiddenStrategy);

    return (allLoraModels || []).filter(
      (item) => BASETYPE_NAME_TO_VALUE[item.baseType] === BASETYPE_NAME_TO_VALUE[baseType],
    );
  }, [genInfo.loraModels, baseModel, presetBaseModelId]);

  const controlnets = genInfo.controlnets || [];
  const imageReference = genInfo.imageReference;

  const showSourceImage =
    withSourceImage &&
    genInfo.generateType &&
    genInfo.generateType !== 'normal' &&
    genInfo.sourceImage &&
    genInfo.sourceImage.url;

  const showModelsSeciton = !isUpscale && (baseModel || presetBaseModelId || loraModels.length > 0);
  const showControlnets = !isUpscale && controlnets.length > 0;

  const handleClose = () => {
    setTempState({ elementInfoPanelId: null, generationWindowId: null });
    onClose && onClose();
  };

  const hanldeApplyLora = (lora) => {
    applyModel(
      Object.assign({}, lora, {
        baseType: BASETYPE_VALUE_TO_NAME[lora.baseType] || lora.baseType,
      }),
      getActiveGeneratorId(),
      true,
    );

    const loraBaseType = BASETYPE_NAME_TO_VALUE[lora.baseType] || lora.baseType;

    if (loraBaseType !== MODEL_FLUX) {
      setTempState({ globalMode: 'pro' });
      localStorage.setItem('__MODE_SETTINGS_Key__', 'pro');
    }
  };

  const handleApplyReference = () => {
    applyImg2ImgSourceImage(imageReference);
  };

  const handleSendParams = (event) => {
    const type = event.currentTarget.dataset.type;
    const value = event.currentTarget.dataset.value;

    if (type === 'all') {
      setShowAppliedTip(true);
      setTimeout(() => setShowAppliedTip(false), 3000);
    }

    applyGenerateParams(
      {
        ...genInfo,
        isGalleryImage: undefined,
        sourceImage: null,
        seedNumber: acturalSeed,
        projectData: null,
        width: imageWidth,
        height: imageHeight,
        currentValue: value,
      },
      {
        styleIcon: image?.url,
      },
      type,
    );

    // lora中存在非flux也默认打开高级模式
    const outLoraModalsFlag = genInfo?.loraModels?.some((lora) => {
      const loraBaseType = BASETYPE_NAME_TO_VALUE[lora.baseType] || lora.baseType;
      return loraBaseType !== MODEL_FLUX;
    });

    if ((type === 'checkpoint' && !presetBaseModelId) || outLoraModalsFlag) {
      setTempState({ globalMode: 'pro' });
      localStorage.setItem('__MODE_SETTINGS_Key__', 'pro');
    }
  };

  const copyText = (text) => {
    if (!text) {
      return;
    }
    copy(text).then(
      () => {
        notifySuccess(translate('copy_success_message'));
      },
      () => {
        notifyError(translate('copy_fail_message'));
      },
    );
  };

  const handleViewLora = async (lora) => {
    if (lora.modelUuid) {
      window.open(`${communityUrl}/modelinfo/${lora.modelUuid}`, '_blank');
    } else if (lora.uuid) {
      requestModelVersionDetail(lora.uuid)
        .then((res) => {
          window.open(`${communityUrl}/modelinfo/${res.data.modelUuid}`, '_blank');
        })
        .catch(() => {
          // ...
        });
    }
  };

  const handleSendToCanvas = () => {
    insertGalleryImage(image, true);
  };

  const handleReproduce = () => {
    if (isInpaint) {
      reproduceInpaint(genInfo);
    } else if (isCollage) {
      reproduceCollage(genInfo);
    } else if (isUpscale) {
      reproduceUpscale(genInfo);
    } else if (isOutpaint) {
      reproduceOutpaint(genInfo);
    }
  };

  // 符合条件的才显示重新编辑按钮
  const showReproduceButton =
    !noReproduce &&
    ((isInpaint && inpaintData) ||
      (isCollage && collageData) ||
      (isUpscale && upscaleData) ||
      (isOutpaint && outpaintData));
  const factorText = () => {
    const detailFactor = genInfo.detailFactor;
    if (detailFactor) {
      return upscaleOptions[detailFactor === 0.05 ? 1 : 0].label;
    } else {
      return '';
    }
  };

  // cn选择主体后，图生图不可用
  const currentNodeId = useProjectState('currentNodeId');
  const generator = useProjectState((state) => state.nodeMap[currentNodeId])?.data;
  const hasImg2img = !!generator?.data?.imageReference?.url;
  const currentCnConflictWithImg2img =
    hasImg2img &&
    !!genInfo?.controlnets?.some((cn) => {
      const cnInfo = controlNetList.find((item) => item.value === cn.type);
      return cnInfo && cnInfo.img2imgUnsupport;
    });
  const disabledSendToGenerator = currentCnConflictWithImg2img;

  return (
    <div className="h-[500px] flex flex-col justify-between" onClick={(e) => e.stopPropagation()}>
      {withHeader && (
        <h5 className="sub-drag-handle flex items-center h-11 pt-0.5 px-4 cursor-move border-b border-[#efefef]">
          <span className="flex-1 text-[#2D2D2D] text-xs font-semibold">{translate('generation_info')}</span>
          <button onClick={() => handleClose()} className="cursur-pointer">
            <Icon className="icon-border_close-1" />
          </button>
        </h5>
      )}
      <div className="overflow-scroll">
        <div className="px-4 py-2.5 tiny-scrollbar overflow-auto flex-1 border-b border-[#efefef]">
          {!!imageReference && (
            <>
              <h5 id="img2img-entry" className="text-[#2d2d2d] text-xs leading-[14px] font-bold font-gro">
                {translate('prompt_imgtoimg')}
              </h5>
              <div className="flex items-center h-[66px] -mx-2 mt-1 px-2 rounded group hover:bg-[#F4F6F8]">
                <img
                  src={convert2CompressedUrl(imageReference.url, 132)}
                  className="w-[50px] h-[50px] bg-[#f7f7f7] rounded object-cover"
                />
                <span className="flex-1 ml-2 text-xs text-[#2d2d2d] leading-4 text-ellipsis overflow-hidden whitespace-nowrap">
                  {translate('cn_weight')}:{imageReference.strength}
                </span>
                <button
                  onClick={handleApplyReference}
                  className="opacity-0 group-hover:opacity-100 flex-shrink-0 w-12 h-6 ml-4 px-2 text-[#2d2d2d] text-xs font-normal bg-primary/10 border border-primary rounded-sm"
                >
                  {translate('common_use')}
                </button>
              </div>
              <div className="-mx-4 mt-2 mb-2.5 border-b border-[#efefef]" />
            </>
          )}
          {showSourceImage && (
            <div className="flex items-center justify-between h-5">
              <span className="text-[#2D2D2D] text-xs font-semibold">{translate('originalimage')}</span>
            </div>
          )}
          {!showSourceImage ? null : isInpaint ? (
            <InpaintSourceImage sourceImage={genInfo.sourceImage} inpaintData={inpaintData} />
          ) : isCollage ? (
            <CollageSourceImages collageData={collageData} />
          ) : (
            <img
              draggable={false}
              src={convert2CompressedUrl(genInfo.sourceImage.url, 512)}
              className="w-full h-auto max-h-[200px] mt-1 mb-3 bg-[#ececec] object-contain"
            />
          )}
          {!isUpscale && (
            <div className="flex items-center justify-between h-5">
              <span className="text-[#2D2D2D] text-xs font-semibold">{translate('prompt')}</span>
              {withSendButtons && (
                <div>
                  <button className="" onClick={() => copyText(genInfo.prompt)}>
                    <Icon className="shk-tiqutishici" fontSize={16} color="#888" />
                  </button>
                  <button data-type="prompt" className="ml-2" onClick={handleSendParams}>
                    <Icon className="icon-shakker-fasongdaoshengchengqi" fontSize={16} color="#999" />
                  </button>
                </div>
              )}
            </div>
          )}
          {!isUpscale && (
            <p className="tiny-scrollbar break-words whitespace-pre-wrap overflow-auto tiny-scrollbar max-h-[120px] -mx-2 px-2 py-1 hover:bg-[#f8f8f8] rounded-sm text-xs text-[#2d2d2d]">
              {genInfo.prompt}
            </p>
          )}
          {acturalPrompt && (
            <div className="mt-[15px] mb-[10px] cursor-pointer">
              <div className="flex items-center justify-between h-5">
                <span className="text-[#7F7F7F] text-xs" onClick={toggle}>
                  {translate('prompt_improved')}
                </span>
                <div>
                  <button className="ml-2" onClick={() => copyText(acturalPrompt)}>
                    <Icon className="shk-tiqutishici" fontSize={16} color="#888" />
                  </button>
                  <Icon
                    className={`shk-kuangjia-zhankai ml-2 ${opened ? 'rotate-180' : ''}`}
                    fontSize={16}
                    color="#999"
                    onClick={toggle}
                  />
                </div>
              </div>
              <Collapse in={opened}>
                <p className="mt-[12px] text-xs text-[#2D2D2D] break-words whitespace-pre-wrap">{acturalPrompt}</p>
              </Collapse>
            </div>
          )}
          {showModelsSeciton &&
            (presetBaseModelId ? (
              <>
                <div className="-mx-4 my-2.5 border-t border-[#efefef]" />
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <h5
                      id="model-checkpoints-entry"
                      className="text-[#2d2d2d] text-xs  leading-[14px] font-bold font-gro"
                    >
                      {translate('model_title_checkpoint')}
                      <span className="text-[#b2b2b2]">&ensp;Checkpoint</span>
                    </h5>
                    <button data-type="checkpoint" onClick={handleSendParams}>
                      <Icon className="icon-shakker-fasongdaoshengchengqi" fontSize={16} color="#888" />
                    </button>
                  </div>
                  <PresetModelCard id={presetBaseModelId} readonly={true} />
                </div>
              </>
            ) : (
              baseModel && (
                <>
                  <div className="-mx-4 my-2.5 border-t border-[#efefef]" />
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <h5
                        id="model-checkpoints-entry"
                        className="text-[#2d2d2d] text-xs leading-[14px] font-bold font-gro"
                      >
                        {translate('model_title_checkpoint')}
                        <span className="text-[#b2b2b2]">&ensp;Checkpoint</span>
                      </h5>
                      <button data-type="checkpoint" onClick={handleSendParams}>
                        <Icon className="icon-shakker-fasongdaoshengchengqi" fontSize={16} color="#888" />
                      </button>
                    </div>
                    <CustomModelCard data={baseModel} readonly={true} isSimpleMode={isSimple} />
                  </div>
                </>
              )
            ))}
          {showModelsSeciton && !!loraModels?.length && (
            <>
              <div className="-mx-4 my-2.5 border-t border-[#efefef]" />
              <div className="flex items-center justify-between h-5">
                <h5 id="model-checkpoints-entry" className="text-[#2d2d2d] text-xs  leading-[14px] font-bold font-gro">
                  {translate('model_title_lora')}
                  <span className="text-[#b2b2b2]">&ensp;LoRA</span>
                </h5>
                {!isSimple && withSendButtons && (
                  <button data-type="loras" onClick={handleSendParams}>
                    <Icon className="icon-shakker-fasongdaoshengchengqi" fontSize={16} color="#888" />
                  </button>
                )}
              </div>
              <div className="flex flex-col items-strength space-y-1 mt-2">
                {loraModels?.map((lora) => (
                  <div
                    key={lora?.versionId}
                    className="relative flex items-center -mx-2 p-2 hover:bg-[#f8f8f8] rounded"
                  >
                    <div className="flex-shrink-0 relative h-[52px] w-[40px] overflow-hidden rounded">
                      <a onClick={() => handleViewLora(lora)} className="w-10 h-[52px] cursor-pointer" target="_blank">
                        <img className="w-10 h-[52px] object-cover" src={convert2CompressedUrl(lora?.imageUrl, 80)} />
                      </a>
                      {!!lora?.versionName && (
                        <span className="absolute bottom-0 right-0 left-0 h-4 bg-black/40 backdrop-blur text-center text-xs text-white">
                          {lora?.versionName}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 flex flex-col items-stretch ml-2 overflow-hidden">
                      <div className="flex items-center">
                        {lora?.isHiddenStrategy && <img src={iconLoraRecommend} className="mr-1" />}
                        <span className="flex-1 text-xs text-[#2d2d2d] whitespace-nowrap text-ellipsis overflow-hidden text-xs">
                          {lora?.name}
                        </span>
                      </div>
                      <div className="flex items-center mt-2">
                        <span className="text-xs text-[#8c8c8c]">{lora?.weight}</span>
                        {!isSimple && (
                          <>
                            <div className="w-px h-2.5 mx-2 bg-[#efefef]" />
                            <span className="text-xs text-[#8c8c8c]">
                              {`${BASETYPE_VALUE_TO_SHORT_LABEL[lora?.baseType] || lora?.baseType}`.replace(/\s/g, '')}
                            </span>
                          </>
                        )}
                        {lora?.vipUsed === 1 && <div className="w-px h-2.5 mx-2 bg-[#efefef]" />}
                        {lora?.vipUsed === 1 && <img src={crownIcon} className="w-[13px]" alt="" />}
                      </div>
                    </div>

                    <button
                      onClick={() => hanldeApplyLora(lora)}
                      className="flex-shrink-0 w-12 h-6 ml-4 px-2 text-[#2d2d2d] text-xs font-normal bg-primary/10 border border-primary rounded-sm"
                    >
                      {translate('common_use')}
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
          {showControlnets && (
            <>
              <div className="-mx-4 my-2.5 border-t border-[#efefef]" />
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-[#2D2D2D] text-xs font-semibold">{translate('image_tool_reference')}</span>
                  <span className="text-[#B2B2B2] text-xs ml-[8px] font-semibold">Control-Net</span>
                </div>
                {withSendButtons && (
                  <Tooltip
                    disabled={!disabledSendToGenerator}
                    withArrow
                    arrowOffset={20}
                    position="top-end"
                    className="scale-90"
                    label={
                      disabledSendToGenerator
                        ? translate('imgref_dimension_conflict_with_img2img')
                        : translate('generate_prams_sent_tip')
                    }
                  >
                    <button data-type="references" onClick={handleSendParams} disabled={disabledSendToGenerator}>
                      <Icon className="icon-shakker-fasongdaoshengchengqi" fontSize={16} color="#888" />
                    </button>
                  </Tooltip>
                )}
              </div>
              <div className="mt-3 space-y-2">
                {controlnets
                  .filter((item) => !!item.sourceImage?.url)
                  .map((item, index) => (
                    <div key={index} className="flex items-center">
                      <img
                        src={convert2CompressedUrl(item.sourceImage.url, 72)}
                        className="w-9 h-9 bg-[#f7f7f7] border border-[#e4e4e4] rounded object-contain"
                      />
                      <span className="max-w-[116px] ml-[8px] text-xs text-[#383838] font-medium leading-4 text-ellipsis overflow-hidden whitespace-nowrap">
                        {controlnetMap[item.type]?.label || item.type}
                      </span>
                      <div className="mx-[8px] h-[10px] border-r border-[#EFEFEF]" />
                      <span className="flex-1 text-xs text-[#383838] leading-4 text-ellipsis overflow-hidden whitespace-nowrap">
                        {translate('cn_weight')}:{item.controlWeight}
                      </span>
                    </div>
                  ))}
              </div>
            </>
          )}
          {isUpscale && !!upscaleData && (
            <>
              <div className="-mx-4 my-2.5 border-t border-[#efefef]" />
              <div className="flex items-center justify-between mb-2">
                <span className="text-[#2D2D2D] text-xs font-semibold">{translate('imagetool_upscale_scale')}</span>
                <span className="text-[#2D2D2D] text-xs font-semibold">{upscaleData.scale}x</span>
              </div>
              <div className="flex items-center justify-between mb-2">
                <span className="flex-1 text-[#383838] text-xs font-medium">{translate('imagetool_upscale_size')}</span>
                <span className="text-xs text-[#999]">
                  {genInfo.sourceImage.width}x{genInfo.sourceImage.height}
                </span>
                <Icon className="icon-pro_arrow_right mx-1 scale-90" size={10} color="#999" />
                <span className="text-xs text-[#999]">
                  {genInfo.width}*{genInfo.height}
                </span>
              </div>
              <div className="flex items-center justify-between mb-2">
                <span className="flex-1 text-[#383838] text-xs font-medium">{translate('upscale_tile_type')}</span>
                <span className="text-xs text-[#999]">{factorText()}</span>
              </div>
            </>
          )}
          {!isSimple && !isUpscale && !isOutpaint && (
            <>
              <div className="-mx-4 my-2.5 border-t border-[#efefef]" />
              <div className="flex items-center justify-between h-5">
                <span className="text-[#2D2D2D] text-xs font-semibold">{translate('parameter_title')}</span>
                {withSendButtons && !isStarFlow && (
                  <button data-type="params" onClick={handleSendParams}>
                    <Icon className="icon-shakker-fasongdaoshengchengqi" fontSize={16} color="#888" />
                  </button>
                )}
              </div>
              <div className="mt-2 space-y-2">
                {paramsNames.map((key) => (
                  <div key={key} className="flex items-center justify-between">
                    <span className="w-[116px] text-xs text-[#7f7f7f] font-medium leading-4 text-ellipsis overflow-hidden whitespace-nowrap">
                      {translate('parameter_' + key)}
                    </span>
                    <span className="text-xs text-[#2d2d2d] leading-4 text-ellipsis overflow-hidden whitespace-nowrap">
                      {key === 'samplerMethod'
                        ? samplerMethodNamesMap[genInfo[key]] || genInfo[key]
                        : genInfo[key] || '-'}
                    </span>
                  </div>
                ))}
                {!isStarFlow && (
                  <div className="flex items-center justify-between">
                    <span className="w-[116px] text-xs text-[#7f7f7f] font-medium leading-4 text-ellipsis overflow-hidden whitespace-nowrap">
                      {translate('parameter_seedNumber')}
                    </span>
                    <span className="text-xs text-[#2d2d2d] leading-4 text-ellipsis overflow-hidden whitespace-nowrap">
                      {acturalSeed || '-'}
                    </span>
                  </div>
                )}
                {genInfo.negativePrompt && (
                  <div className="flex flex-col">
                    <span className="w-[116px] text-xs text-[#7f7f7f] font-medium leading-4 text-ellipsis overflow-hidden whitespace-nowrap">
                      {translate('prompt_negative')}
                    </span>
                    <p className="tiny-scrollbar break-words whitespace-pre-wrap overflow-auto tiny-scrollbar max-h-[120px] -mx-2 px-2 mt-1 py-1 hover:bg-[#f8f8f8] rounded-sm text-xs text-[#2d2d2d]">
                      {genInfo.negativePrompt}
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        {!!genInfo?.styleCode && (
          <>
            <div className="flex justify-between items-center px-4 pt-4">
              <h5 className="text-[#2D2D2D] text-xs font-semibold">{translate('style_code')}</h5>
              <div className="flex items-center justify-between leading-[0]">
                <button className="ml-2" onClick={() => copyText(`#style: ${genInfo?.styleCode},`)}>
                  <Icon className="shk-tiqutishici" fontSize={16} color="#888" />
                </button>
                <button
                  data-type="styleCode"
                  className="ml-2"
                  data-value={genInfo?.styleCode}
                  onClick={handleSendParams}
                >
                  <Icon className="icon-shakker-fasongdaoshengchengqi" fontSize={16} color="#888" />
                </button>
              </div>
            </div>
            <div className="mt-3 px-4 text-[#7F7F7F] text-[12px] leading-4">{genInfo.styleCode}</div>
            <div className="-mx-4 my-2.5 border-t border-[#efefef]" />
          </>
        )}
        <div className="p-4">
          <h5 className="text-[#2D2D2D] text-xs font-semibold">{translate('setting_size')}</h5>
          <div className="flex items-center justify-between mt-2">
            <FrameSize disabled value={{ isFixedRatio: false }} />
            <SizeInputGroup disabled value={{ width: imageWidth, height: imageHeight }} />
          </div>
        </div>
      </div>
      <footer className="flex flex-col items-stretch p-4 bg-[#FAFAFA]">
        {!withFooter ? null : reproduceButtonLabels[genInfo.generateType] && showReproduceButton ? (
          <div className="flex items-center">
            {!genInfo.isGalleryImage && (
              <div className="flex cursor-pointer mr-[8px]">
                <ImageShareButton imageInfo={image}>
                  <div className="flex items-center justify-center w-[38px] h-[38px] bg-white rounded-[2px] border border-[#DCDCDC]">
                    <Icon className="icon-External_Link" fontSize={16} color="#2d2d2d" />
                  </div>
                </ImageShareButton>
              </div>
            )}
            <button
              onClick={handleReproduce}
              disabled={genInfo.generateType?.includes('collage')}
              className="h-[38px] flex-1 bg-primary rounded-[2px] disabled:opacity-30"
            >
              <span className="text-xs text-[#2d2d2d] font-medium">{reproduceButtonLabels[genInfo.generateType]}</span>
            </button>
          </div>
        ) : withSendButtons ? (
          <div className="flex items-center">
            {genInfo.isGalleryImage ? (
              <button
                onClick={handleSendToCanvas}
                className="h-[38px] mr-2 px-2 bg-white border border-[#DCDCDC] rounded-[2px]"
              >
                <span className="text-xs text-[#2D2D2D] font-medium">{translate('gallery_send_to_canvas')}</span>
              </button>
            ) : (
              <div className="flex cursor-pointer mr-[8px]">
                <ImageShareButton imageInfo={image}>
                  <div className="flex items-center justify-center w-[38px] h-[38px] bg-white rounded-[2px] border border-[#DCDCDC]">
                    <Icon className="icon-External_Link" fontSize={16} color="#2d2d2d" />
                  </div>
                </ImageShareButton>
              </div>
            )}
            <Tooltip
              disabled={!showAppliedTip && !disabledSendToGenerator}
              withArrow
              arrowOffset={20}
              position="top-end"
              className="scale-90"
              label={
                disabledSendToGenerator
                  ? translate('imgref_dimension_conflict_with_img2img')
                  : translate('generate_prams_sent_tip')
              }
            >
              <button
                disabled={disabledSendToGenerator}
                data-type="all"
                onClick={handleSendParams}
                className={classNames(
                  'flex-1 h-[38px] bg-[#2c2c2c] text-white rounded-[2px] flex-1',
                  disabledSendToGenerator ? 'opacity-40 cursor-not-allowed' : '',
                )}
              >
                <span className="text-xs font-medium">{translate('send_to_generator')}</span>
              </button>
            </Tooltip>
          </div>
        ) : null}
        {!!genInfo.authorAvatar && (
          <div className="flex items-center mt-4">
            <span className="text-sm font-normal text-black/60">{translate('image_author')}</span>
            <img
              className="w-[18px] h-[18px] ml-2 rounded-full"
              src={convert2CompressedUrl(genInfo.authorAvatar, 36)}
            />
            <span className="ml-1 text-xs text-black font-normal">{genInfo.authorNickname}</span>
          </div>
        )}
      </footer>
    </div>
  );
};

export const GenerationInfoPanelInnerWorkflow = ({ genInfo }) => {
  const [showAppliedTip, setShowAppliedTip] = useState(false);

  const { nodes = [] } = genInfo;

  const handleClose = () => {
    setTempState({ elementInfoPanelId: null });
  };

  const hanldeApplyLora = (lora) => {
    applyModel(
      Object.assign({}, lora, {
        baseType: BASETYPE_VALUE_TO_NAME[lora.baseType] || lora.baseType,
      }),
      getActiveGeneratorId(),
      true,
    );

    const loraBaseType = BASETYPE_NAME_TO_VALUE[lora.baseType] || lora.baseType;

    if (loraBaseType !== MODEL_FLUX) {
      updateTempState({ globalMode: 'pro' });
      localStorage.setItem('__MODE_SETTINGS_Key__', 'pro');
    }
  };

  const handleSendParams = (type, value) => {
    if (type === 'all') {
      setShowAppliedTip(true);
      setTimeout(() => setShowAppliedTip(false), 3000);
    } else if (type === 'model') {
      if (value.presetId) {
        applyPresetModel(value.presetId);
      } else {
        applyModel(value);
      }
    }
  };

  const handleUseWorkflow = () => {
    enterWorkflowMode(genInfo);
  };

  return (
    <>
      <h5 className="sub-drag-handle flex items-center h-11 px-4 py-3 cursor-move border-b border-[#efefef]">
        <span className="flex-1 text-[#2D2D2D] text-xs font-semibold">{translate('generation_info')}</span>
        <span className="flex items-center">
          <Icon className="shk-info text-[#8c8c8c]" />
          <span className="text-[#8c8c8c] text-xs font-normal">由工作流生成</span>
        </span>
        <button onClick={handleClose} className="cursur-pointer relative top-[-1px] right-[-7px]">
          <Icon className="icon-border_close-1" color="#222" />
        </button>
      </h5>
      <div className="px-4 py-3 border-b border-[#efefef]">
        <h2 className="text-[#2d2d2d] text-xs font-semibold pb-3">{translate('flow_g_flow')}</h2>
        <div className={cs('text-[#2d2d2d] font-normal text-xs', styles.title)}>{genInfo.title}</div>
        {!!genInfo.userInfo && (
          <div className="flex items-center mt-2.5">
            <div
              className="size-[24px] overflow-hidden bg-contain flex-shrink-0 rounded-full"
              style={{ backgroundImage: `url(${convert2CompressedUrl(genInfo.userInfo?.icon)})` }}
            />
            <div className={cs('ml-2', styles.name)}>{genInfo.userInfo?.name}</div>
          </div>
        )}
      </div>
      <div className="overflow-auto tiny-scrollbar max-h-96">
        {nodes.map((node, idx) => {
          return node.fields.map((field, idy) => {
            const FieldComponent = getFieldViewComponent(field.type);
            return (
              <FieldComponent
                key={idx + idy}
                {...field}
                onSendParams={handleSendParams}
                onApplyLora={hanldeApplyLora}
              />
            );
          });
        })}
      </div>
      <footer className="flex flex-col items-stretch p-4 bg-[#FAFAFA]">
        <Tooltip
          disabled={!showAppliedTip}
          withArrow
          arrowOffset={20}
          position="top-end"
          className="scale-90"
          label={translate('generate_prams_sent_tip')}
        >
          <button onClick={handleUseWorkflow} className="h-[38px] bg-[#13E6B3] rounded-[2px]">
            <span className="text-xs text-[#005848] font-medium">{translate('send_to_generator')}</span>
          </button>
        </Tooltip>
      </footer>
    </>
  );
};

export const GenerationInfoPanel = ({ elementId }) => {
  const image = getProjectState('elements').find((item) => item.id === elementId);
  const genInfo = useElementGenInfo(image);
  if (!image || !genInfo) return null;
  const type = genInfo.generateType;

  const width = Math.round(image?.width * (image.scaleX || 1));
  const height = Math.round(image?.height * (image.scaleY || 1));

  return type == 'workflow' ? (
    <GenerationInfoPanelInnerWorkflow genInfo={genInfo} />
  ) : (
    <GenerationInfoPanelInner image={image} genInfo={genInfo} imageWidth={width} imageHeight={height} noReproduce />
  );
};
